import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Moon, Sun, Download, Menu, X } from 'lucide-react';
import Home from './pages/Home';
import About from './pages/About';
import Projects from './pages/Projects';
import Services from './pages/Services';
import Contact from './pages/Contact';
import Demo from './pages/Demo'; // 👈 Page Demo
import SupemirProject from './pages/projects/SupemirProject';
import KastlerProject from './pages/projects/KastlerProject';
import EcommerceProject from './pages/projects/EcommerceProject';
import AbsProject from './pages/projects/AbsProject';
import AzureProject from './pages/projects/AzureProject';

// Composant principal avec la navigation
function AppContent() {
  const [darkMode, setDarkMode] = useState(true);
  const [language, setLanguage] = useState('fr');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDarkMode = () => setDarkMode(!darkMode);
  const toggleLanguage = () => setLanguage(language === 'fr' ? 'ar' : 'fr');
  const isRTL = language === 'ar';

  const navigateTo = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // Déterminer la page active basée sur le pathname
  const isActivePage = (page) => {
    if (page === 'home') return location.pathname === '/';
    if (page === 'projects') return location.pathname.startsWith('/project');
    return location.pathname === `/${page}`;
  };

  return (
    <div
      className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} transition-all duration-300`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Navbar */}
      <nav
        className={`fixed w-full top-0 z-50 ${darkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-sm border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <button
                onClick={() => navigateTo('/')}
                className={`text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent ${
                  isRTL ? 'font-arabic' : ''
                }`}
              >
                ERRAKI ABDELMAJID
              </button>
            </div>

            {/* Desktop Menu - SANS la démo */}
            <div className="hidden lg:flex items-center gap-6">
              {[
                { page: 'home', path: '/' },
                { page: 'about', path: '/about' },
                { page: 'projects', path: '/projects' },
                { page: 'services', path: '/services' },
                { page: 'contact', path: '/contact' },
              ].map(({ page, path }) => (
                <button
                  key={page}
                  onClick={() => navigateTo(path)}
                  className={`hover:text-blue-500 transition-colors font-medium ${
                    isActivePage(page) ? 'text-blue-500' : ''
                  }`}
                >
                  {{
                    home: language === 'fr' ? 'Accueil' : 'الرئيسية',
                    about: language === 'fr' ? 'À propos' : 'من أنا',
                    projects: language === 'fr' ? 'Projets' : 'المشاريع',
                    services: language === 'fr' ? 'Services' : 'الخدمات',
                    contact: language === 'fr' ? 'Contact' : 'اتصل بي',
                  }[page]}
                </button>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className={`px-4 py-2 rounded-lg font-medium ${
                  darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
                } transition-colors`}
              >
                {language === 'fr' ? 'العربية' : 'Français'}
              </button>
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg ${
                  darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
                } transition-colors`}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <a
                href="/pdf/CV Erraki Abdelmajid.pdf"
                download="CV_Erraki_Abdelmajid.pdf"
                className={`px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2 ${
                  darkMode 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-700 hover:shadow-blue-600/50 text-white' 
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:shadow-blue-500/50 text-white'
                }`}
              >
                <Download size={18} />
                {language === 'fr' ? 'Télécharger CV' : 'تحميل السيرة'}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu - SANS la démo */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3">
              {[
                { page: 'home', path: '/' },
                { page: 'about', path: '/about' },
                { page: 'projects', path: '/projects' },
                { page: 'services', path: '/services' },
                { page: 'contact', path: '/contact' },
              ].map(({ page, path }) => (
                <button
                  key={page}
                  onClick={() => navigateTo(path)}
                  className={`block w-full px-4 py-2 rounded-lg font-medium ${
                    darkMode ? 'bg-gray-800' : 'bg-gray-100'
                  } text-center`}
                >
                  {{
                    home: language === 'fr' ? 'Accueil' : 'الرئيسية',
                    about: language === 'fr' ? 'À propos' : 'من أنا',
                    projects: language === 'fr' ? 'Projets' : 'المشاريع',
                    services: language === 'fr' ? 'Services' : 'الخدمات',
                    contact: language === 'fr' ? 'Contact' : 'اتصل بي',
                  }[page]}
                </button>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={toggleLanguage}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    darkMode ? 'bg-gray-800' : 'bg-gray-100'
                  } text-center`}
                >
                  {language === 'fr' ? 'العربية' : 'Français'}
                </button>
                <button
                  onClick={toggleDarkMode}
                  className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} flex items-center justify-center gap-2`}
                >
                  {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                  {darkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
              </div>
              <a
                href="/pdf/CV Erraki Abdelmajid.pdf"
                download="CV_Erraki_Abdelmajid.pdf"
                className={`block w-full px-4 py-3 rounded-lg font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 ${
                  darkMode 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-700 hover:shadow-blue-600/50 text-white' 
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:shadow-blue-500/50 text-white'
                }`}
              >
                <Download size={18} />
                {language === 'fr' ? 'Télécharger CV' : 'تحميل السيرة'}
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Routes - Page Content */}
      <div className="pt-20">
        <Routes>
          <Route path="/" element={<Home darkMode={darkMode} language={language} />} />
          <Route path="/about" element={<About darkMode={darkMode} language={language} />} />
          <Route path="/projects" element={<Projects darkMode={darkMode} language={language} navigateTo={navigateTo} />} />
          <Route path="/services" element={<Services darkMode={darkMode} language={language} />} />
          <Route path="/contact" element={<Contact darkMode={darkMode} language={language} />} />
          
          {/* 👇 ROUTE DEMO - Accessible uniquement via URL */}
          <Route path="/demo" element={<Demo darkMode={darkMode} language={language} />} />
          
          {/* Routes des projets */}
          <Route path="/project-supemir" element={<SupemirProject darkMode={darkMode} language={language} onBack={() => navigateTo('/projects')} />} />
          <Route path="/project-kastler" element={<KastlerProject darkMode={darkMode} language={language} onBack={() => navigateTo('/projects')} />} />
          <Route path="/project-ecommerce" element={<EcommerceProject darkMode={darkMode} language={language} onBack={() => navigateTo('/projects')} />} />
          <Route path="/project-abs" element={<AbsProject darkMode={darkMode} language={language} onBack={() => navigateTo('/projects')} />} />
          <Route path="/project-azure" element={<AzureProject darkMode={darkMode} language={language} onBack={() => navigateTo('/projects')} />} />
        </Routes>
      </div>

      {/* Footer */}
      <footer
        className={`${darkMode ? 'bg-gray-800' : 'bg-white'} border-t ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        } py-8 mt-20`}
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500">© 2025 Abdelmajid Erraki. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// Composant principal avec Router
export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}